// Brand · Staff Management → Commission Tiers (iter-f31).
//
// One Tiered Commission Profile per staff member: a set of treatments,
// a base rate, and up to four escalating monthly revenue thresholds.
// Crossing a threshold lifts the rate for the WHOLE calendar month
// (retroactively) on the selected treatments only — every other
// treatment keeps the standard per-service rule from the staff wizard.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Layers, Plus, Pencil, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import TierProfileDialog from "./commissionTiers/TierProfileDialog";

export default function CommissionTiersTab() {
  const [profiles, setProfiles] = useState([]);
  const [month, setMonth] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u, s, c, b] = await Promise.all([
        staffApi.get("/commission-tiers"),
        staffApi.get("/staff-users"),
        staffApi.get("/services", { params: { active_only: false } }),
        staffApi.get("/categories"),
        staffApi.get("/branches"),
      ]);
      setProfiles(p.data?.profiles || []);
      setMonth(p.data?.month || "");
      setStaffUsers((u.data || []).filter((x) => x.active !== false));
      setServices(s.data || []);
      setCategories(c.data || []);
      setBranches(b.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fm = useCallback(
    (n) => formatBranchMoney(branches[0] || null, Number(n) || 0),
    [branches],
  );

  const takenStaffIds = useMemo(
    () => new Set(profiles.map((p) => p.staff_id)),
    [profiles],
  );

  const remove = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Delete the tiered commission profile for ${p.staff_name}? Commission already recorded in past months stays exactly as it was paid.`,
    )) return;
    try {
      await staffApi.delete(`/commission-tiers/${p.staff_id}`);
      toast.success(`Profile removed for ${p.staff_name}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const monthLabel = useMemo(() => {
    if (!month) return "";
    const [y, m] = month.split("-");
    try {
      return new Date(Number(y), Number(m) - 1, 1)
        .toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    } catch { return month; }
  }, [month]);

  return (
    <div className="space-y-4" data-testid="commission-tiers-tab">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" /> Tiered Commission Profiles
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-3xl">
            One profile per staff member. Revenue from the ticked treatments is tracked across the
            Bali calendar month on each treatment's <strong>base price</strong> (before service
            charge and PPN), multiplied by the revenue share set for that treatment. The moment a
            threshold is crossed, the higher rate is applied back across the entire month.
            Treatments outside a profile keep their standard rate from the staff wizard.
          </p>
        </div>
        <Button
          size="sm"
          className="gap-1.5 h-9"
          onClick={() => setEditing({ staff_id: "", base_rate: "", tiers: [], service_ids: [] })}
          data-testid="tier-new-profile-btn"
        >
          <Plus className="w-4 h-4" /> New Profile
        </Button>
      </div>

      <Card className="rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="uppercase text-[10px] tracking-wider">Staff</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Treatments</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Base</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Thresholds</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">
                  {monthLabel ? `${monthLabel} so far` : "This month"}
                </TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10" data-testid="tier-empty-state">
                    {loading ? "Loading…" : "No tiered profiles yet. Create one to escalate a staff member's rate as their monthly revenue grows."}
                  </TableCell>
                </TableRow>
              ) : profiles.map((p) => {
                const cm = p.current_month || {};
                const reached = Number(cm.tier_index || 0);
                return (
                  <TableRow key={p.staff_id} data-testid={`tier-profile-row-${p.staff_id}`}>
                    <TableCell className="text-sm font-medium">
                      {p.staff_name}
                      {p.position ? (
                        <span className="block text-[11px] text-muted-foreground font-normal">{p.position}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground" data-testid={`tier-svc-count-${p.staff_id}`}>
                      {p.service_count} treatment{p.service_count === 1 ? "" : "s"}
                      {(() => {
                        const split = Object.values(p.service_shares || {})
                          .filter((v) => Number(v) > 0 && Number(v) < 100).length;
                        return split > 0 ? (
                          <span className="block text-[11px]">{split} shared</span>
                        ) : null;
                      })()}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums" data-testid={`tier-base-${p.staff_id}`}>
                      {Number(p.base_rate)}%
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.tiers || []).length === 0 && (
                          <span className="text-xs text-muted-foreground">Flat base rate</span>
                        )}
                        {(p.tiers || []).map((t, i) => (
                          <Badge
                            key={`${p.staff_id}-t${i}`}
                            variant="outline"
                            className={`rounded-full text-[10px] font-normal ${reached >= i + 1 ? "border-emerald-300 bg-emerald-50 text-emerald-800" : ""}`}
                            data-testid={`tier-chip-${p.staff_id}-${i + 1}`}
                          >
                            {fm(t.threshold)} → {Number(t.rate)}%
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`tier-month-state-${p.staff_id}`}>
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <TrendingUp className="w-3 h-3 text-muted-foreground" />
                        {fm(cm.revenue || 0)}
                      </span>
                      <span className="block text-[11px] text-muted-foreground mt-0.5">
                        {reached > 0 ? `Tier ${reached} · ${Number(cm.tier_rate || 0)}%` : `Base · ${Number(cm.tier_rate || p.base_rate || 0)}%`}
                        {cm.next_threshold
                          ? ` · ${fm(Math.max(0, cm.next_threshold - (cm.revenue || 0)))} to ${Number(cm.next_rate)}%`
                          : " · top tier"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1"
                          onClick={() => setEditing({
                            staff_id: p.staff_id,
                            base_rate: String(p.base_rate ?? ""),
                            tiers: (p.tiers || []).map((t) => ({
                              threshold: String(t.threshold), rate: String(t.rate),
                            })),
                            service_ids: [...(p.service_ids || [])],
                            service_shares: { ...(p.service_shares || {}) },
                          })}
                          data-testid={`tier-edit-${p.staff_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-red-600 hover:text-red-700"
                          onClick={() => remove(p)}
                          data-testid={`tier-delete-${p.staff_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <TierProfileDialog
          draft={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          staffUsers={staffUsers}
          takenStaffIds={takenStaffIds}
          services={services}
          categories={categories}
          currency={branches[0] || null}
        />
      )}
    </div>
  );
}
